import { Row, Cell } from '@tanstack/react-table';
import classnames from 'classnames';
import * as React from 'react';

import { DynamoTablePagination } from './components/DynamoTablePagination';
import { TableDateSectionProps } from './components/TableDateSection';
import { TableHeaderActionsProps } from './components/TableHeaderActions';
import { TablePagination, TablePaginationProps } from './components/TablePagination';
import { TableSkeleton } from './components/TableSkeleton';
import {
  TableHeaders,
  TableFooters,
  TableFilters,
  TableActions,
  TableRows,
  LastRowColumn,
} from './components/Wrappers';
import styles from './Table.module.scss';
import { TableProvider, TableProviderProps, TableRefType } from './TableContext';

type TableProps<TData> = {
  lastRow?: LastRowColumn[];
  isLoading?: boolean;
  skeletonRows?: number;
  showFooter?: boolean;
  showHeader?: boolean;
  paginationOptions?: TablePaginationProps & {
    hasDynamoSource?: boolean;
    lastEvaluatedKey?: string;
    onLastEvaluatedKeyChange?: (newKey?: string) => void;
  };
  selectAllLabel?: string;
  onRowClick?: (
    event: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
    cell: Cell<TData, unknown>,
    row: Row<TData>,
  ) => void;
  rowHrefFactory?: (row: Row<TData>) => string | null;
  /**
   *  When `isFilterable='custom' use this to specify columns which
   *  will still be handled client side, avoid using this where possible
   */
  shouldFillPageHeight?: boolean;
  tableClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  headerActionsProps?: TableHeaderActionsProps;
  dateSectionProps?: Partial<TableDateSectionProps>;
  /**
   * Test id for Playwright and other test suites, maps to `data-testid`
   */
  testId?: string;
};

function Table<TData>(
  props: TableProviderProps<TData> & TableProps<TData>,
  ref: React.Ref<TableRefType>,
) {
  const {
    isLoading,
    skeletonRows,
    columns,
    lastRow,
    showHeader = true,
    showFooter = false,
    isPaginated,
    paginationOptions,
    isSelectable,
    selectAllOnePageAtATime,
    selectAllLabel,
    onRowClick,
    rowHrefFactory,
    isFilterable,
    shouldFillPageHeight = false,
    tableClassName,
    headerClassName,
    bodyClassName,
    headerActionsProps,
    dateSectionProps,
    testId = 'table',
  } = props;

  const tablePaginationRef = React.useRef(null);

  React.useImperativeHandle(
    ref,
    () => ({
      getPaginationState: () => tablePaginationRef.current,
    }),
    [],
  );

  return (
    <TableProvider {...props}>
      <div className={shouldFillPageHeight ? styles.pageHeightContainer : undefined}>
        <TableActions headerActionsProps={headerActionsProps} dateSectionProps={dateSectionProps} />
        <div className={styles.overflowContainer}>
          <table
            cellPadding={0}
            cellSpacing={0}
            className={classnames(styles.table, tableClassName)}
            data-testid={testId}>
            {showHeader && (
              <TableHeaders
                headerClassName={headerClassName}
                selectAllOnePageAtATime={selectAllOnePageAtATime}
              />
            )}
            <tbody className={bodyClassName} key="table-body">
              {showHeader && isFilterable && <TableFilters selectAllLabel={selectAllLabel} />}
              {isLoading ? (
                <TableSkeleton
                  columns={columns}
                  isSelectable={isSelectable}
                  skeletonRows={skeletonRows}
                />
              ) : (
                <TableRows
                  lastRow={lastRow}
                  onRowClick={onRowClick}
                  rowHrefFactory={rowHrefFactory}
                />
              )}
            </tbody>
            {showFooter && <TableFooters />}
          </table>
        </div>
        {isPaginated &&
          (paginationOptions?.hasDynamoSource ? (
            <DynamoTablePagination {...paginationOptions} ref={tablePaginationRef} />
          ) : (
            <TablePagination {...paginationOptions} />
          ))}
      </div>
    </TableProvider>
  );
}

const reffed = React.forwardRef(Table);

export { reffed as Table };
